// ============================================================================
// AXIOS CLIENT
// ============================================================================
// Configured axios instance with interceptors for all API calls

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { apiConfig } from '../config/apiConfig';
import { setupRequestInterceptor, setupResponseInterceptor } from './interceptors';

/**
 * Create and configure axios instance
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: apiConfig.baseURL,
    timeout: apiConfig.timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Setup interceptors
  setupRequestInterceptor(instance);
  setupResponseInterceptor(instance);

  return instance;
};

/**
 * Main axios instance for API calls
 */
export const axiosClient = createAxiosInstance();

/**
 * HTTP Methods Helper Functions
 * These functions provide a cleaner API for making requests
 */

/**
 * GET request
 */
export const get = <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosClient.get<T>(url, config);
};

/**
 * POST request
 */
export const post = <T = any, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosClient.post<T>(url, data, config);
};

/**
 * PUT request
 */
export const put = <T = any, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosClient.put<T>(url, data, config);
};

/**
 * PATCH request
 */
export const patch = <T = any, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosClient.patch<T>(url, data, config);
};

/**
 * DELETE request
 */
export const del = <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosClient.delete<T>(url, config);
};

/**
 * Upload file with progress tracking
 */
export const uploadFile = <T = any>(
  url: string,
  file: File,
  onUploadProgress?: (progressEvent: any) => void
): Promise<AxiosResponse<T>> => {
  const formData = new FormData();
  formData.append('file', file);

  return axiosClient.post<T>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress,
  });
};

/**
 * Download file
 */
export const downloadFile = (
  url: string,
  filename?: string
): Promise<void> => {
  return axiosClient
    .get(url, {
      responseType: 'blob',
    })
    .then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        filename || `download-${Date.now()}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
};

/**
 * Export axios instance and helpers
 */
export default axiosClient;
